import {
  AlertType,
  AssignmentType,
  IPurchaseOrderLine,
  ISalesOrderLine,
  ISalesOrderLineAssignment,
  ISalesOrderLineAssignmentInput,
} from '../../../graphql/schema/p4types';
import EditableDataGrid from '../../EditableDataGrid';
import React, { useContext } from 'react';
import { ICommit, IEditableDataGridColumnDef } from '../../../@types/editableDataGridTypes';
import DateFormatter from '../../grid-addons/formatters/DateFormatter';
import NumericFormatter from '../../grid-addons/formatters/NumericFormatter';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import ConnectedFormContext from '../../Context/ConnectedFormContext';
import { useDispatch } from 'react-redux';
import { serviceActions } from '../../../state/modules/service';
import { InfoButton } from '../../connectedForm/Button';
import { uuIdGenerator } from '../../../utils/UuIdGenerator';
import { makeStyles } from '@fluentui/react-components';
import { Pill, PillContainer } from '../../Pill/Pill';

//PurchaseOrderLine extension as we're adding a 'newAssignment' field to the grid here to allow manual entry of assignment quantities.
type IExtendedPurchaseOrderLine = IPurchaseOrderLine & {
  newAssignment: number | string; //Can be a string due to text entry, but should be treated as a number.
  assignedToOtherOrders: number;
};

const usePurchaseOrderLineColumns: (
  shortage: number,
  totalAssigned: number
) => Array<IEditableDataGridColumnDef<IExtendedPurchaseOrderLine>> = (shortage, totalAssigned) => {
  const dispatch = useDispatch();

  const getDefaultAssignmentValue: (column: any, rowData: IExtendedPurchaseOrderLine) => number = (
    column: any,
    rowData
  ) => {
    const toAssign: number = shortage - totalAssigned;
    if (toAssign <= 0) {
      dispatch(
        serviceActions.genericAlert(
          'Fully assigned',
          'Sales Order Line is already assigned in full.',
          AlertType.Warning
        )
      );
      return 0;
    }

    const availableToAssign: number = (rowData?.onOrder || 0) - rowData.assignedToOtherOrders;

    //If there are more required than on order, default the full on order amount.
    //Otherwise, can default to the full amount required for assignment.
    return toAssign >= availableToAssign ? availableToAssign : toAssign;
  };

  return [
    {
      name: 'Order Number',
      key: 'purchaseOrder.orderNumber',
      editable: false,
      width: 80,
    },
    {
      name: 'Supplier',
      key: 'purchaseOrder.supplier.name',
      editable: false,
    },
    {
      name: 'Expected Date',
      key: 'purchaseOrder.targetDate',
      editable: false,
      formatter: DateFormatter,
    },
    {
      name: 'On Order',
      key: 'onOrder',
      editable: false,
    },
    {
      name: 'Assigned (Other Orders)',
      key: 'assignedToOtherOrders',
      editable: false,
    },
    {
      name: 'Assign',
      key: 'newAssignment',
      formatter: NumericFormatter,
      editable: true,
      getDefaultValue: getDefaultAssignmentValue,
    },
  ];
};

export const usePurchaseOrderAssignedTotal: (salesOrderLine?: ISalesOrderLine) => number = (salesOrderLine) => {
  return (
    salesOrderLine?.assignments
      ?.filter((assignment) => assignment?.assignmentType !== AssignmentType.Shortage) //Shortage type assignments should be excluded.
      ?.reduce((previousValue, currentValue) => previousValue + parseFloat(currentValue?.assigned), 0) || 0
  );
};

const useStyles = makeStyles({
  pill: {
    position: 'absolute',
    right: '37px',
    marginBottom: '-20px',
  },
});

export const AssignPurchaseOrdersGrid = ({
  purchaseOrderLines,
  salesOrderLine,
  rowIdx,
  shortage,
  ignoreKeypresses,
}: {
  purchaseOrderLines: Array<IPurchaseOrderLine>;
  salesOrderLine?: ISalesOrderLine;
  rowIdx: number;
  shortage: number;
  ignoreKeypresses: boolean;
}) => {
  const allPurchaseOrderLineAssignments: Array<ISalesOrderLineAssignment> =
    (salesOrderLine?.assignments as Array<ISalesOrderLineAssignment>) ?? [];
  //Filter out archived assignments - We should create a new assignment instead of re-using the archived one.
  const purchaseOrderLineAssignments = allPurchaseOrderLineAssignments.filter((assignment) => !assignment.archived);

  const totalAssigned = usePurchaseOrderAssignedTotal(salesOrderLine);

  const columns = usePurchaseOrderLineColumns(shortage, totalAssigned);
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  const styles = useStyles();

  if (purchaseOrderLines.length === 0) return null;

  const onRowUpdated = (commit: ICommit<IExtendedPurchaseOrderLine>) => {
    const existingAssignmentLineIndex = purchaseOrderLineAssignments.findIndex(
      (value) => value.purchaseOrderLine?.id === commit.original?.id
    );

    if (existingAssignmentLineIndex >= 0) {
      const updatedPurchaseOrderLineAssignments = [...purchaseOrderLineAssignments];
      updatedPurchaseOrderLineAssignments[existingAssignmentLineIndex].assigned = commit.updated?.newAssignment;
      //Clear assignment lines if zeroed.
      updatedPurchaseOrderLineAssignments[existingAssignmentLineIndex].deleted =
        parseFloat(commit.updated?.newAssignment as string) === 0;
      updatedPurchaseOrderLineAssignments[existingAssignmentLineIndex].updated = true;
      dispatch(connectedForm.change(`orderLines.${rowIdx}.assignments`, updatedPurchaseOrderLineAssignments));
    } else {
      const newPurchaseOrderLineAssignment: ISalesOrderLineAssignmentInput = {
        purchaseOrderLine: normalizeVariables(commit.original, ['newAssignment', 'assignedToOtherOrders']),
        assigned: commit.updated?.newAssignment || 0,
        assignmentType: AssignmentType.PurchaseOrder,
        uuid: uuIdGenerator(),
      };

      dispatch(
        connectedForm.change(`orderLines.${rowIdx}.assignments`, [
          ...purchaseOrderLineAssignments,
          newPurchaseOrderLineAssignment,
        ])
      );
    }
  };

  const listData = purchaseOrderLines
    .map((purchaseOrderLine) => {
      const assignedThisLine = purchaseOrderLineAssignments
        .filter(
          (purchaseOrderLineAssignment) => purchaseOrderLineAssignment.purchaseOrderLine?.id === purchaseOrderLine.id
        )
        .reduce((previousValue, currentValue) => previousValue + parseFloat(currentValue.assigned), 0);

      //Need to get the amount assigned to other orders to prevent over-assignment on a given PO.
      const assignedToOtherOrders: number =
        purchaseOrderLine.assignments
          ?.filter((assignment) => assignment?.salesOrderLine?.id !== salesOrderLine?.id)
          .reduce((previousValue, currentValue) => previousValue + (currentValue?.assigned || 0), 0) || 0;

      const extendedPurchaseOrderLine: IExtendedPurchaseOrderLine = {
        ...purchaseOrderLine,
        newAssignment: assignedThisLine,
        assignedToOtherOrders: assignedToOtherOrders,
      };

      return extendedPurchaseOrderLine;
    })
    .filter((purchaseOrderLine) => purchaseOrderLine.onOrder - purchaseOrderLine.assignedToOtherOrders > 0);

  const automaticAssignment = () => {
    let toBeAssigned = shortage;
    if (toBeAssigned <= 0) {
      dispatch(serviceActions.genericAlert('Fully assigned', 'This line is already fully assigned', AlertType.Warning));
      return;
    }

    const updatedAssignments: Array<ISalesOrderLineAssignmentInput> = [
      ...(purchaseOrderLineAssignments as Array<ISalesOrderLineAssignmentInput>),
    ];

    listData.forEach((extendedPurchaseOrderLine: IExtendedPurchaseOrderLine) => {
      const existingAssignmentLineIndex = purchaseOrderLineAssignments.findIndex(
        (value) => value.purchaseOrderLine?.id === extendedPurchaseOrderLine?.id
      );

      const availableToAssign: number =
        (extendedPurchaseOrderLine?.onOrder || 0) - extendedPurchaseOrderLine.assignedToOtherOrders;
      const toAssignThisLine = toBeAssigned >= availableToAssign ? availableToAssign : toBeAssigned;

      if (existingAssignmentLineIndex >= 0) {
        updatedAssignments[existingAssignmentLineIndex].assigned = toAssignThisLine;
        //Clear assignment lines if zeroed.
        updatedAssignments[existingAssignmentLineIndex].deleted = toAssignThisLine === 0;
      } else if (toAssignThisLine > 0) {
        updatedAssignments.push({
          purchaseOrderLine: normalizeVariables(extendedPurchaseOrderLine, ['newAssignment', 'assignedToOtherOrders']),
          assigned: toAssignThisLine,
          assignmentType: AssignmentType.PurchaseOrder,
          uuid: uuIdGenerator(),
        });
      }
      toBeAssigned -= toAssignThisLine;
    });

    dispatch(connectedForm.change(`orderLines.${rowIdx}.assignments`, updatedAssignments));
  };

  return (
    <>
      <PillContainer>
        <InfoButton style={{ padding: '6px 5px 10px 5px' }} onClick={automaticAssignment}>
          Auto Assign Purchase Orders
        </InfoButton>
        <Pill className={styles.pill}>
          {shortage} short - {totalAssigned} assigned
        </Pill>
      </PillContainer>
      <EditableDataGrid
        columns={columns}
        listData={listData}
        rowsCount={listData?.length || 0}
        contentHeight={200}
        enableCellSelect={true}
        onRowUpdated={onRowUpdated}
        ignoreKeypresses={ignoreKeypresses}
      />
    </>
  );
};
